// PWA-оболочка в окне (SPEC §4.0 шаг 4, §4.4 deep-link, §4.5 badge/install/update). Обёртка над
// pwa-shell.PwaProvider (класс с инъецируемым PwaEnvironment). app-ui собирает окружение из браузерных
// API + transport.push (PushPort) + navigate-роутера, регистрирует SW, подписывается на SW→окно
// сообщение `{type:'navigate', deepLink}` и проводит его в роутер (deep-link к сообщению, FR-NOTIF-04).
//
// Всё взаимодействие с window/SW защищено проверками наличия — провайдер монтируется и без браузера
// (SSR/тесты), просто без эффектов. Push/badge идут СТРОГО через transport (инв.2): своего fetch нет.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PwaProvider,
  detectStandalone,
  isApplePlatform,
  isServiceWorkerScript,
  type PwaEnvironment,
  type NavigateMessage,
  type PushManagerLike,
  type NotificationPermissionState,
  type BadgeApi,
  type PushPort,
} from '@tele-chat/pwa-shell'
import { useTransport } from '../transport/TransportContext.js'

/** Реактивные флаги оболочки (данные состояния, без экшенов). */
interface PwaFlags {
  /** null, если оболочка не инициализирована (вне браузера). */
  pwa: PwaProvider | null
  /** Можно ли показать кнопку «Установить» (реактивно). */
  canInstall: boolean
  /** Доступно ли обновление SW (реактивно). */
  updateReady: boolean
  /** Запущено как установленное PWA. */
  isStandalone: boolean
  /** Поддержка Web Push в среде. */
  pushSupported: boolean
  /** Текущее разрешение на уведомления (обновляется refreshPush после enable/disable). */
  pushPermission: NotificationPermissionState
  /** Есть ли активная подписка на push на ЭТОМ устройстве (реактивно). */
  pushEnabled: boolean
}

export interface PwaValue extends PwaFlags {
  /** Перечитать состояние push (разрешение + факт подписки) — звать после enable/disablePush. */
  refreshPush: () => Promise<void>
}

const PwaContext = createContext<PwaValue>({
  pwa: null,
  canInstall: false,
  updateReady: false,
  isStandalone: false,
  pushSupported: false,
  pushPermission: 'default',
  pushEnabled: false,
  refreshPush: async () => {},
})

/** Собирает PwaEnvironment из браузерных зондов + транспорта (push) + navigate. */
function buildEnvironment(push: PushPort, navigate: (deepLink: string) => void): PwaEnvironment {
  const nav = navigator
  const win = window
  const displayModeStandalone =
    typeof win.matchMedia === 'function' && win.matchMedia('(display-mode: standalone)').matches
  const navigatorStandalone = (nav as unknown as { standalone?: boolean }).standalone
  const isStandalone = detectStandalone({ displayModeStandalone, navigatorStandalone })
  const isIOS = isApplePlatform({
    platform: nav.platform ?? '',
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    hasMSStream: 'MSStream' in win,
  })
  const pushSupported = 'serviceWorker' in nav && 'PushManager' in win
  const permission: NotificationPermissionState =
    typeof Notification !== 'undefined' ? (Notification.permission as NotificationPermissionState) : 'default'

  const badgeApi: BadgeApi = {
    setAppBadge: (n) => (nav as unknown as BadgeApi).setAppBadge?.(n),
    clearAppBadge: () => (nav as unknown as BadgeApi).clearAppBadge?.(),
  }

  // PushManager берётся у активной SW-регистрации. Браузерные типы структурно шире локального
  // PushManagerLike (endpoint/toJSON/unsubscribe есть у настоящего PushSubscription) — приводим через unknown.
  type SubLike = Awaited<ReturnType<PushManagerLike['getSubscription']>>
  const pushManager: PushManagerLike = {
    async getSubscription() {
      const reg = await nav.serviceWorker?.ready
      const sub = await reg?.pushManager.getSubscription()
      return (sub as unknown as SubLike | null) ?? null
    },
    async subscribe(options) {
      const reg = await nav.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: options.userVisibleOnly,
        // pwa-shell отдаёт ключ как Uint8Array; браузерный applicationServerKey ждёт BufferSource.
        applicationServerKey: options.applicationServerKey as unknown as BufferSource,
      })
      return sub as unknown as NonNullable<SubLike>
    },
  }

  return {
    pushEnv: { pushSupported, isStandalone, isIOS, permission },
    badgeApi,
    pushManager,
    transport: push,
    requestPermission: async (): Promise<NotificationPermissionState> => {
      if (typeof Notification === 'undefined') return 'denied'
      return (await Notification.requestPermission()) as NotificationPermissionState
    },
    navigate,
    reload: () => win.location.reload(),
  }
}

export interface PwaProviderShellProps {
  children: ReactNode
  /** Путь к service worker (по умолчанию /sw.js — отдаёт api-server/reverse-proxy). */
  swUrl?: string
  /** Отключить реальную регистрацию SW/слушатели (тесты/SSR). */
  enabled?: boolean
}

/**
 * Монтирует PWA-оболочку. Создаёт PwaProvider (если есть браузер), регистрирует SW, проводит
 * SW→окно navigate в роутер, слушает install/update-события, обновляет реактивные флаги для UI.
 */
export function PwaProviderShell({
  children,
  swUrl = '/sw.js',
  enabled = true,
}: PwaProviderShellProps): JSX.Element {
  const transport = useTransport()
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const [state, setState] = useState<PwaFlags>({
    pwa: null,
    canInstall: false,
    updateReady: false,
    isStandalone: false,
    pushSupported: false,
    pushPermission: 'default',
    pushEnabled: false,
  })
  const pwaRef = useRef<PwaProvider | null>(null)

  // Перечитать live-разрешение + факт активной подписки (после enable/disablePush или на монтаже).
  // pushEnabled и pushPermission меняет ТОЛЬКО этот путь — sync() их сохраняет, чтобы install/update-
  // события не сбрасывали состояние push.
  const refreshPush = useCallback(async (): Promise<void> => {
    const pwa = pwaRef.current
    if (pwa === null) return
    const permission: NotificationPermissionState =
      typeof Notification !== 'undefined'
        ? (Notification.permission as NotificationPermissionState)
        : 'default'
    let enabled = false
    try {
      enabled = await pwa.isPushSubscribed()
    } catch {
      enabled = false
    }
    setState((prev) => ({ ...prev, pushPermission: permission, pushEnabled: enabled }))
  }, [])

  useEffect(() => {
    if (!enabled) return
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return

    const env = buildEnvironment(transport.push, (deepLink) => navigateRef.current(deepLink))
    const pwa = new PwaProvider(env)
    pwaRef.current = pwa

    const sync = (): void =>
      setState((prev) => ({
        pwa,
        canInstall: pwa.canInstall,
        updateReady: pwa.updateReady,
        isStandalone: pwa.isStandalone,
        pushSupported: pwa.pushSupported,
        // push-состояние ведёт refreshPush (live-разрешение + факт подписки) — не сбрасываем его
        // на install/update-событиях, иначе кнопка/блок настроек «мигают» в выключенное.
        pushPermission: prev.pushPermission,
        pushEnabled: prev.pushEnabled,
      }))
    sync()
    void refreshPush()

    // — install (§4.7) —
    const onBeforeInstall = (e: Event): void => {
      pwa.onBeforeInstallPrompt(e as unknown as Parameters<PwaProvider['onBeforeInstallPrompt']>[0])
      sync()
    }
    const onAppInstalled = (): void => {
      pwa.onAppInstalled()
      sync()
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onAppInstalled)

    // — SW регистрация + update + навигация по deep-link (§4.3/§4.4) —
    const cleanups: Array<() => void> = []
    if ('serviceWorker' in navigator) {
      const onMessage = (event: MessageEvent): void => {
        const data = event.data as NavigateMessage | undefined
        if (data && data.type === 'navigate') {
          pwa.onSwMessage(data)
        }
      }
      navigator.serviceWorker.addEventListener('message', onMessage)
      cleanups.push(() => navigator.serviceWorker.removeEventListener('message', onMessage))

      const onControllerChange = (): void => pwa.onControllerChange()
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
      cleanups.push(() =>
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange),
      )

      // Регистрируем SW только если по swUrl реально отдаётся JS-скрипт. Иначе (когда SW не выложен в
      // статике, SPA-fallback вернёт index.html с text/html) браузер пишет в консоль «unsupported MIME
      // type» ещё ДО reject промиса — .catch это НЕ подавляет. Пробуем content-type заранее и тихо
      // пропускаем регистрацию, если SW нет: приложение полноценно работает без офлайн-оболочки.
      void (async () => {
        // Зонд доступности SW (в pwa-shell — app-ui сеть в обход транспорта не делает, инв.2).
        if (!(await isServiceWorkerScript(swUrl))) return
        try {
          const reg = await navigator.serviceWorker.register(swUrl)
          const checkWaiting = (): void => {
            if (reg.waiting) {
              pwa.onSwWaiting(reg)
              sync()
            }
          }
          checkWaiting()
          reg.addEventListener('updatefound', () => {
            const installing = reg.installing
            if (installing) {
              installing.addEventListener('statechange', () => {
                if (installing.state === 'installed') checkWaiting()
              })
            }
          })
        } catch {
          // SW не зарегистрировался (например, http без https) — приложение работает без офлайн-оболочки.
        }
      })()
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onAppInstalled)
      for (const c of cleanups) c()
      pwaRef.current = null
    }
  }, [enabled, transport, swUrl, refreshPush])

  const value = useMemo<PwaValue>(() => ({ ...state, refreshPush }), [state, refreshPush])

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>
}

/** Доступ к состоянию PWA-оболочки (флаги install/update + сам PwaProvider). */
export function usePwa(): PwaValue {
  return useContext(PwaContext)
}
